// Cue scheduler: audible 1-5-tick cues from core's `cuesBetween`, on the AudioContext clock.
//
// A short timer looks HORIZON_S ahead and schedules each cue as an oscillator started at an exact context
// time, so the sound does not depend on the timer's jitter. The window's song ranges come from the one clock
// (`playSpans`), so a cue lands at the same context time as the music's beat at any speed and across a loop
// wrap. A seek, a speed change, a loop change or a new map calls `reset`, which silences the tones not yet
// started and plans again from the new clock. The planning (`planCues`) is pure and unit-tested.

import { cuesBetween, type Cue, type CueKind, type CueMode, type CountMap } from '@salsacount/core';
import { playSpans, songToCtx, type Clock } from './clock.js';

/** How far ahead of the context clock cues are scheduled, seconds. */
export const HORIZON_S = 0.15;
/** Timer period, milliseconds: well under the horizon so every window is planned before it is needed. */
export const TICK_MS = 50;

export interface PlannedCue {
  /** Context time at which the cue sounds. */
  readonly when: number;
  readonly cue: Cue;
}

/** The cues that sound in context time [from, to) under this clock and map, in order. Consecutive windows give each cue once. */
export function planCues(clock: Clock, map: CountMap, mode: CueMode, from: number, to: number): PlannedCue[] {
  const planned: PlannedCue[] = [];
  if (mode === 'off') return planned;
  for (const span of playSpans(clock, from, to)) {
    for (const cue of cuesBetween(map, span.song0, span.song1, mode)) {
      planned.push({ when: span.ctx0 + (cue.t - span.song0) / span.rate, cue });
    }
  }
  return planned;
}

/** Pitch, length and loudness of each cue: count 1 the highest and strongest, 5 the next, the rest a soft short tick. */
export const TONES: Readonly<Record<CueKind, { readonly hz: number; readonly seconds: number; readonly gain: number }>> = {
  one: { hz: 1320, seconds: 0.12, gain: 0.9 },
  five: { hz: 990, seconds: 0.1, gain: 0.65 },
  tick: { hz: 660, seconds: 0.035, gain: 0.3 },
};

/** The replay tick (a tap's own time marked in a replay): high, short and a square wave, so it cannot be taken for a cue. Fixed loudness: it sounds with the cues off. */
export const MARK_TONE = { hz: 2400, seconds: 0.05, gain: 0.7 } as const;

/** One replay tick that was scheduled: the song time it marks, and the context time it was set to sound at (for tests and diagnostics). */
export interface MarkTick {
  /** Song seconds: the stored time of the tap. */
  readonly song: number;
  /** Context time the oscillator was started at: where the clock puts `song`. */
  readonly when: number;
  /** The clock's rate at that moment (song seconds per context second). */
  readonly rate: number;
  /** Context time when it was scheduled. */
  readonly scheduledAt: number;
}

/** The context time at which song position `song` sounds under this clock, or null (paused, or already past): where a replay tick is started. */
export function planMark(clock: Clock, song: number): number | null {
  return songToCtx(clock, song);
}

interface Pending {
  readonly when: number;
  readonly osc: OscillatorNode;
  readonly env: GainNode;
}

export class CueScheduler {
  private timer = 0;
  private until = 0;
  private pending: Pending[] = [];
  private marks: Pending[] = [];
  private readonly out: GainNode;
  private readonly markOut: GainNode;

  constructor(
    private readonly ctx: AudioContext,
    private readonly source: () => { readonly clock: Clock; readonly map: CountMap | null; readonly mode: CueMode },
    volume = 0.6,
  ) {
    this.out = ctx.createGain();
    this.out.gain.value = volume;
    this.out.connect(ctx.destination);
    this.markOut = ctx.createGain();
    this.markOut.connect(ctx.destination);
  }

  /**
   * A replay tick at song position `song`, started on the context clock exactly where the clock puts it (`planMark`: the same conversion the cues use, so it
   * stays on the music at any speed and across a loop). It is not a cue: it ignores the cue mode and volume, and `reset` leaves it alone (a replay owns it:
   * `clearMarks`). Returns what was scheduled, or null when the clock is not playing or the position has passed.
   */
  mark(song: number): MarkTick | null {
    const { clock } = this.source();
    const when = planMark(clock, song);
    if (when === null) return null;
    const osc = this.ctx.createOscillator();
    const env = this.ctx.createGain();
    osc.type = 'square';
    osc.frequency.value = MARK_TONE.hz;
    env.gain.setValueAtTime(0, when);
    env.gain.linearRampToValueAtTime(MARK_TONE.gain, when + 0.001);
    env.gain.exponentialRampToValueAtTime(0.001, when + MARK_TONE.seconds);
    osc.connect(env).connect(this.markOut);
    osc.onended = () => { osc.disconnect(); env.disconnect(); };
    osc.start(when);
    osc.stop(when + MARK_TONE.seconds + 0.01);
    this.marks.push({ when, osc, env });
    return { song, when, rate: clock.rate, scheduledAt: this.ctx.currentTime };
  }

  /** Silence the replay ticks that have not sounded yet (the replay was stopped). */
  clearMarks(): void {
    const now = this.ctx.currentTime;
    for (const p of this.marks) {
      if (p.when > now) {
        p.osc.onended = null;
        try { p.osc.stop(); } catch { /* already stopped */ }
        p.osc.disconnect();
        p.env.disconnect();
      }
    }
    this.marks = [];
  }

  setVolume(v: number): void {
    this.out.gain.value = Math.min(1, Math.max(0, v));
  }

  start(): void {
    if (this.timer) return;
    this.timer = window.setInterval(() => this.tick(), TICK_MS);
    this.tick();
  }

  stop(): void {
    window.clearInterval(this.timer);
    this.timer = 0;
    this.silence();
  }

  /** The clock, the map or the mode changed: drop the tones not yet started and plan again from now. */
  reset(): void {
    this.silence();
    this.until = 0;
    if (this.timer) this.tick();
  }

  private silence(): void {
    const now = this.ctx.currentTime;
    for (const p of this.pending) {
      if (p.when > now) {
        p.osc.onended = null;
        try { p.osc.stop(); } catch { /* already stopped */ }
        p.osc.disconnect();
        p.env.disconnect();
      }
    }
    this.pending = this.pending.filter((p) => p.when <= now);
  }

  private tick(): void {
    const { clock, map, mode } = this.source();
    const now = this.ctx.currentTime;
    this.pending = this.pending.filter((p) => p.when + 1 > now);
    if (!clock.playing || !map) { this.until = 0; return; }
    const from = Math.max(this.until, now);
    const to = now + HORIZON_S;
    if (!(to > from)) return;
    for (const { when, cue } of planCues(clock, map, mode, from, to)) this.sound(when, cue.kind);
    this.until = to;
  }

  private sound(when: number, kind: CueKind): void {
    const tone = TONES[kind];
    const osc = this.ctx.createOscillator();
    const env = this.ctx.createGain();
    osc.frequency.value = tone.hz;
    env.gain.setValueAtTime(0, when);
    env.gain.linearRampToValueAtTime(tone.gain, when + 0.002);
    env.gain.exponentialRampToValueAtTime(0.001, when + tone.seconds);
    osc.connect(env).connect(this.out);
    osc.onended = () => { osc.disconnect(); env.disconnect(); };
    osc.start(when);
    osc.stop(when + tone.seconds + 0.01);
    this.pending.push({ when, osc, env });
  }
}
